import React, { useState, useEffect } from 'react';
import { 
  Users, TrendingUp, DollarSign, Calendar, MapPin, 
  BarChart3, Target, Award, Activity, ChevronRight,
  ArrowUp, ArrowDown, Minus, RefreshCw, UserCheck,
  Utensils, Heart, AlertTriangle, Shield
} from 'lucide-react';
import { ManagementSystem } from '../utils/managementSystem';
import { UserStorage } from '../utils/userStorage';
import { MeetingStorage } from '../utils/meetingStorage';
import { StaffStorage } from '../utils/staffStorage';
import { CostCalculation, CostUtils } from '../utils/costCalculation';
import { AttendeeListGenerator } from '../utils/attendeeListGenerator';
import ComplianceMonitor from './ComplianceMonitor';
import StatCard from './StatCard';
import { logger } from '../utils/logger';

const ManagerDashboard = ({ setCurrentView, setSelectedRepId }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState(null);
  const [territoryAnalysis, setTerritoryAnalysis] = useState([]);
  const [roiMetrics, setRoiMetrics] = useState([]);
  const [staffAnalytics, setStaffAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState(30); // days
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadManagerData();
  }, [timeframe]);

  const loadManagerData = async () => {
    setLoading(true);
    
    try {
      const user = UserStorage.getCurrentUser();
      if (!user || user.userType !== 'manager') {
        setLoading(false);
        return;
      }

      setCurrentUser(user);

      // Get all users and meetings for analysis
      const allUsers = UserStorage.getAllUsers();
      const allMeetings = MeetingStorage.getAllMeetings();

      // Calculate team performance
      const performance = ManagementSystem.calculateTeamPerformance(user.id, allMeetings, allUsers);
      setTeamPerformance(performance);

      // Analyze territories
      const territories = ManagementSystem.analyzeTerritory(user.id, allUsers, allMeetings);
      setTerritoryAnalysis(territories);

      // Calculate ROI metrics
      if (performance) {
        const rois = ManagementSystem.calculateROIMetrics(performance);
        setRoiMetrics(rois);
      }

      // Calculate staff analytics
      const staffData = calculateStaffAnalytics(allMeetings, allUsers);
      setStaffAnalytics(staffData);

    } catch (error) {
      logger.error('Error loading manager data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRepClick = (repId) => {
    setSelectedRepId(repId);
    setCurrentView('rep-detail');
  };

  const calculateStaffAnalytics = (meetings, users) => {
    try {
      const staffMeetings = meetings.filter(m => m.hasStaff && m.staffCount > 0);
      
      const analytics = {
        totalMeetingsWithStaff: staffMeetings.length,
        totalStaffAttendees: staffMeetings.reduce((sum, m) => sum + (m.staffCount || 0), 0),
        averageStaffPerMeeting: 0,
        totalStaffCost: 0,
        averageStaffCost: 0,
        costSavingsVsIndividual: 0,
        staffUtilizationRate: 0,
        topPhysiciansWithStaff: [],
        staffDietaryBreakdown: {
          vegetarian: 0,
          vegan: 0,
          glutenFree: 0,
          allergens: {}
        },
        complianceWithStaff: {
          compliantMeetings: 0,
          nonCompliantMeetings: 0,
          averageCostPerPerson: 0
        }
      };

      if (staffMeetings.length > 0) {
        analytics.averageStaffPerMeeting = analytics.totalStaffAttendees / staffMeetings.length;
        
        // Calculate staff costs and compliance
        let totalCost = 0;
        let totalAttendees = 0;
        let compliantCount = 0;
        
        staffMeetings.forEach(meeting => {
          const staffOrders = StaffStorage.getStaffOrdersForMeeting(meeting.id);
          const costData = CostCalculation.calculateMeetingCost(meeting, staffOrders);
          
          totalCost += costData.totalStaffCost;
          totalAttendees += costData.totalAttendees;
          
          if (costData.complianceStatus.isCompliant) {
            compliantCount++;
          }
        });
        
        analytics.totalStaffCost = totalCost;
        analytics.averageStaffCost = totalCost / analytics.totalStaffAttendees;
        analytics.complianceWithStaff.compliantMeetings = compliantCount;
        analytics.complianceWithStaff.nonCompliantMeetings = staffMeetings.length - compliantCount;
        analytics.complianceWithStaff.averageCostPerPerson = totalCost / totalAttendees;
        
        // Calculate utilization rate (meetings with staff vs total meetings)
        analytics.staffUtilizationRate = (staffMeetings.length / meetings.length) * 100;
        
        // Get top physicians who use staff in meetings
        const physicianStaffUsage = {};
        staffMeetings.forEach(meeting => {
          if (!physicianStaffUsage[meeting.customerId]) {
            physicianStaffUsage[meeting.customerId] = {
              name: meeting.customer,
              meetings: 0,
              totalStaff: 0,
              totalCost: 0
            };
          }
          physicianStaffUsage[meeting.customerId].meetings++;
          physicianStaffUsage[meeting.customerId].totalStaff += meeting.staffCount || 0;
          
          const staffOrders = StaffStorage.getStaffOrdersForMeeting(meeting.id);
          const costData = CostCalculation.calculateMeetingCost(meeting, staffOrders);
          physicianStaffUsage[meeting.customerId].totalCost += costData.totalStaffCost;
        });
        
        analytics.topPhysiciansWithStaff = Object.values(physicianStaffUsage)
          .sort((a, b) => b.totalStaff - a.totalStaff)
          .slice(0, 5);
      }
      
      return analytics;
    } catch (error) {
      logger.error('Error calculating staff analytics:', error);
      return null;
    }
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up': return <ArrowUp className="w-4 h-4 text-green-600" />;
      case 'down': return <ArrowDown className="w-4 h-4 text-red-600" />;
      default: return <Minus className="w-4 h-4 text-gray-600" />;
    }
  };

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!currentUser || currentUser.userType !== 'manager') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Access Denied</h3>
          <p className="text-red-600">Manager access required to view this dashboard.</p>
        </div>
      </div>
    );
  }

  const teamSummary = teamPerformance?.teamSummary;
  const repPerformances = teamPerformance?.repPerformances || [];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Manager Dashboard
            </h2>
            <p className="text-gray-600">
              Welcome back, {(currentUser.fullName || currentUser.name || 'Manager').split(' ')[0]} • {currentUser.company || 'Company'}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Timeframe Selector */}
            <select
              value={timeframe}
              onChange={(e) => setTimeframe(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            
            <button
              onClick={loadManagerData}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Team Overview', icon: <Users className="w-4 h-4" /> },
            { id: 'performance', label: 'Rep Performance', icon: <TrendingUp className="w-4 h-4" /> },
            { id: 'staff', label: 'Staff Analytics', icon: <UserCheck className="w-4 h-4" /> },
            { id: 'territory', label: 'Territory Analysis', icon: <MapPin className="w-4 h-4" /> },
            { id: 'compliance', label: 'Compliance Settings', icon: <Shield className="w-4 h-4" /> },
            { id: 'roi', label: 'ROI Metrics', icon: <DollarSign className="w-4 h-4" /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Team Overview Tab */}
      {activeTab === 'overview' && teamSummary && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={<Users className="w-8 h-8 text-blue-600" />}
              title="Team Size"
              value={teamSummary.totalReps.toString()}
              change={`Managing ${teamSummary.totalReps} representatives`}
              trend="neutral"
            />
            
            <StatCard
              icon={<Calendar className="w-8 h-8 text-green-600" />}
              title="Total Meetings"
              value={teamSummary.totalMeetings.toString()}
              change={`${teamSummary.avgMeetingsPerRep} avg per rep`}
              trend={teamSummary.totalMeetings > 0 ? "up" : "neutral"}
            />
            
            <StatCard
              icon={<DollarSign className="w-8 h-8 text-purple-600" />}
              title="Total Spending"
              value={ManagementSystem.formatCurrency(teamSummary.totalSpending)}
              change={`${ManagementSystem.formatCurrency(teamSummary.avgSpendingPerRep)} avg per rep`}
              trend={teamSummary.totalSpending > 0 ? "up" : "neutral"}
            />
            
            <StatCard
              icon={<Target className="w-8 h-8 text-orange-600" />}
              title="Unique Physicians"
              value={teamSummary.totalUniquePhysicians.toString()}
              change={`${ManagementSystem.formatCurrency(teamSummary.teamCostPerPhysician)} cost per physician`}
              trend={teamSummary.totalUniquePhysicians > 0 ? "up" : "neutral"}
            />
          </div>

          {/* Top Performers */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Team Performance Summary</h3>
            
            {repPerformances.length > 0 ? (
              <div className="space-y-4">
                {repPerformances.slice(0, 5).map((rep, index) => (
                  <div 
                    key={rep.repId}
                    onClick={() => handleRepClick(rep.repId)}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">{index + 1}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{rep.repName}</p>
                        <p className="text-sm text-gray-600">{rep.territory || 'No territory assigned'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm">
                      <div className="text-center">
                        <p className="text-gray-500">Meetings</p>
                        <p className="font-semibold">{rep.totalMeetings}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Weekly Avg</p>
                        <p className="font-semibold">{rep.avgMeetingsPerWeek}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Spending</p>
                        <p className="font-semibold">{ManagementSystem.formatCurrency(rep.totalSpent)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Completion</p>
                        <p className="font-semibold">{Math.round(rep.completionRate)}%</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No team members assigned yet</p>
                <p className="text-sm text-gray-500">Representatives will appear here once assigned to your team</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Individual Rep Performance</h3>
            
            {repPerformances.length > 0 ? (
              <div className="space-y-4">
                {repPerformances.map((rep) => {
                  const trends = ManagementSystem.getPerformanceTrends(rep.repId, MeetingStorage.getAllMeetings());
                  
                  return (
                    <div 
                      key={rep.repId}
                      onClick={() => handleRepClick(rep.repId)}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-800">{rep.repName}</h4>
                          <p className="text-sm text-gray-600">{rep.territory || 'Territory not assigned'}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getTrendIcon(trends.direction)}
                          <span className={`text-sm font-medium ${getTrendColor(trends.direction)}`}>
                            {trends.trendPercentage > 0 ? '+' : ''}{trends.trendPercentage}%
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Total Meetings</p>
                          <p className="text-lg font-semibold text-gray-800">{rep.totalMeetings}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Weekly Average</p>
                          <p className="text-lg font-semibold text-gray-800">{rep.avgMeetingsPerWeek}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Total Spending</p>
                          <p className="text-lg font-semibold text-gray-800">{ManagementSystem.formatCurrency(rep.totalSpent)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Cost per Physician</p>
                          <p className="text-lg font-semibold text-gray-800">{ManagementSystem.formatCurrency(rep.costPerPhysician)}</p>
                        </div>
                      </div>
                      
                      <div className="mt-3 bg-gray-100 rounded-full h-2">
                        <div 
                          className="bg-blue-600 rounded-full h-2" 
                          style={{ width: `${Math.min(rep.completionRate, 100)}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {Math.round(rep.completionRate)}% completion rate
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No performance data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Territory Tab */}
      {activeTab === 'territory' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Territory Analysis</h3>
            
            {territoryAnalysis.length > 0 ? (
              <div className="space-y-4">
                {territoryAnalysis.map((territory, index) => (
                  <div key={territory.territory} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-800">{territory.territory}</h4>
                      <span className="text-sm text-gray-500">{territory.reps.length} reps</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Total Meetings</p>
                        <p className="text-lg font-semibold text-gray-800">{territory.totalMeetings}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Avg per Rep</p>
                        <p className="text-lg font-semibold text-gray-800">{territory.avgMeetingsPerRep}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Total Spending</p>
                        <p className="text-lg font-semibold text-gray-800">{ManagementSystem.formatCurrency(territory.totalSpending)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Cost per Physician</p>
                        <p className="text-lg font-semibold text-gray-800">{ManagementSystem.formatCurrency(territory.costPerPhysician)}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {territory.reps.map((rep) => (
                        <span 
                          key={rep.id}
                          onClick={() => handleRepClick(rep.id)}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm cursor-pointer hover:bg-blue-200 transition-colors"
                        >
                          {rep.name} ({rep.meetings} meetings)
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No territory data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Staff Analytics Tab */}
      {activeTab === 'staff' && staffAnalytics && (
        <div className="space-y-6">
          {/* Staff Overview Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={<UserCheck className="w-8 h-8 text-blue-600" />}
              title="Meetings with Staff"
              value={staffAnalytics.totalMeetingsWithStaff.toString()}
              change={`${staffAnalytics.staffUtilizationRate.toFixed(1)}% utilization rate`}
              trend={staffAnalytics.staffUtilizationRate > 30 ? "up" : "neutral"}
            />
            
            <StatCard
              icon={<Users className="w-8 h-8 text-green-600" />}
              title="Total Staff Attendees"
              value={staffAnalytics.totalStaffAttendees.toString()}
              change={`${staffAnalytics.averageStaffPerMeeting.toFixed(1)} avg per meeting`}
              trend={staffAnalytics.averageStaffPerMeeting > 2 ? "up" : "neutral"}
            />
            
            <StatCard
              icon={<DollarSign className="w-8 h-8 text-purple-600" />}
              title="Staff Meal Cost"
              value={CostUtils.formatCurrency(staffAnalytics.totalStaffCost)}
              change={`${CostUtils.formatCurrency(staffAnalytics.averageStaffCost)} avg per staff`}
              trend={staffAnalytics.averageStaffCost < 25 ? "up" : "down"}
            />
            
            <StatCard
              icon={<Target className="w-8 h-8 text-orange-600" />}
              title="Compliance Rate"
              value={`${((staffAnalytics.complianceWithStaff.compliantMeetings / (staffAnalytics.complianceWithStaff.compliantMeetings + staffAnalytics.complianceWithStaff.nonCompliantMeetings)) * 100).toFixed(1)}%`}
              change={`${staffAnalytics.complianceWithStaff.nonCompliantMeetings} violations`}
              trend={staffAnalytics.complianceWithStaff.nonCompliantMeetings === 0 ? "up" : "down"}
            />
          </div>

          {/* Top Physicians Using Staff */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Physicians with Highest Staff Utilization</h3>
            
            {staffAnalytics.topPhysiciansWithStaff.length > 0 ? (
              <div className="space-y-4">
                {staffAnalytics.topPhysiciansWithStaff.map((physician, index) => (
                  <div key={physician.name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">{index + 1}</span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{physician.name}</p>
                        <p className="text-sm text-gray-600">{physician.meetings} meetings with staff</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm">
                      <div className="text-center">
                        <p className="text-gray-500">Total Staff</p>
                        <p className="font-semibold">{physician.totalStaff}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Avg per Meeting</p>
                        <p className="font-semibold">{(physician.totalStaff / physician.meetings).toFixed(1)}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Staff Cost</p>
                        <p className="font-semibold">{CostUtils.formatCurrency(physician.totalCost)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <UserCheck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No staff data available</p>
                <p className="text-sm text-gray-500">Staff analytics will appear when physicians add staff to meetings</p>
              </div>
            )}
          </div>

          {/* Staff Analytics Summary */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Staff Meeting Analytics</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Cost Analysis */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Cost Analysis</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Average cost per person:</span>
                    <span className="font-medium">{CostUtils.formatCurrency(staffAnalytics.complianceWithStaff.averageCostPerPerson)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Average staff cost:</span>
                    <span className="font-medium">{CostUtils.formatCurrency(staffAnalytics.averageStaffCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Staff utilization rate:</span>
                    <span className="font-medium">{staffAnalytics.staffUtilizationRate.toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              {/* Compliance Analysis */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-800 mb-3">Compliance Overview</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Compliant meetings:</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{staffAnalytics.complianceWithStaff.compliantMeetings}</span>
                      {staffAnalytics.complianceWithStaff.compliantMeetings > 0 && (
                        <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Non-compliant meetings:</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{staffAnalytics.complianceWithStaff.nonCompliantMeetings}</span>
                      {staffAnalytics.complianceWithStaff.nonCompliantMeetings > 0 && (
                        <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      )}
                    </div>
                  </div>
                  <div className="pt-2 border-t border-gray-200">
                    <div className="bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 rounded-full h-2" 
                        style={{ 
                          width: `${((staffAnalytics.complianceWithStaff.compliantMeetings / (staffAnalytics.complianceWithStaff.compliantMeetings + staffAnalytics.complianceWithStaff.nonCompliantMeetings)) * 100)}%` 
                        }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {((staffAnalytics.complianceWithStaff.compliantMeetings / (staffAnalytics.complianceWithStaff.compliantMeetings + staffAnalytics.complianceWithStaff.nonCompliantMeetings)) * 100).toFixed(1)}% compliance rate
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Settings Tab */}
      {activeTab === 'compliance' && (
        <ComplianceMonitor userType="manager" />
      )}

      {/* ROI Tab */}
      {activeTab === 'roi' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">ROI & Cost Effectiveness</h3>
            
            {roiMetrics.length > 0 ? (
              <div className="space-y-4">
                {roiMetrics.map((roi, index) => (
                  <div 
                    key={roi.repId}
                    onClick={() => handleRepClick(roi.repId)}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        {index < 3 && <Award className="w-4 h-4 text-green-600" />}
                        {index >= 3 && <span className="text-green-600 font-semibold">{index + 1}</span>}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{roi.repName}</p>
                        <p className="text-sm text-gray-600">ROI Rank #{index + 1}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm">
                      <div className="text-center">
                        <p className="text-gray-500">ROI Score</p>
                        <p className="font-semibold text-green-600">{roi.roi}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Activity Score</p>
                        <p className="font-semibold">{roi.activityScore}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Efficiency</p>
                        <p className="font-semibold">{roi.costEfficiencyScore}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-500">Penetration</p>
                        <p className="font-semibold">{roi.territoryPenetration}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">No ROI data available</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(ManagerDashboard);